import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

import { PAGE_ROUTES } from "../../utils/stateTypes";
import StandardButton from "../general/StandardButton";
import BackgroundEffect from "../general/BackgroundEffect";


const Calisto = () => {

  return (
    <main>
      <Helmet>
        <title>Callisto | Avior Labs</title>
        <meta
          name={"description"}
          content={"Callisto is our cutting-edge VTOL UAS designed from the ground up as an intelligent, autonomous agent."}
        ></meta>
      </Helmet>
      <BackgroundEffect />
      
      <header className={"drone-image-header"}>
        <h1>CALLISTO</h1>
        <img src={"/images/drones/callisto/callisto-bg2.jpg"} />
      </header>

      <section className={"home-intro-1 standard-width"}>
        <h2 className={"section-head"}>Autonomous. Intelligent. Resilient.</h2>
        <p className={"home-intro-1-text"}>
          Callisto is our cutting-edge VTOL UAS designed from the ground up as an intelligent, autonomous agent. Callisto combines extreme endurance with onboard "AI-at-the-Edge" computing.
        </p>
      </section>

      <section className={"calisto-explain-section"}>
        <div className={"calisto-explain-bg"} />
        <div className={"standard-width"}>
          <h2 className={"section-head"}>Harnessing AI</h2>
          <div className={"calisto-explain-cont"}>
            <p>
              Callisto is designed to be tasked, not flown. At its core lies a dedicated FPGA-based mission computer that revolutionises how you process and receive data. 
              It doesn't just record and stream video, it processes, identifies, and prioritises actionable intelligence in real-time - operating silently and effectively where infrastructure is scarce and bandwidth is limited.
            </p>
            <ul className={"callisto-explain-points"}>
              <li>&#8226; BANDWIDTH EFFICIENT: Designed for environments with limited connectivity, prioritising decision-making data over raw feeds.</li>
              <li>&#8226; SMART TRIAGE: Instead of streaming hours of empty footage, Callisto’s onboard AI identifies threats, anomalies, or specific targets (vehicles, wildlife, infrastructure faults) and transmits only critical "actionable intelligence" packets.</li>
              <li>&#8226; AUTONOMOUS HEALTH MANAGEMENT: AI-driven self-monitoring ensures mission safety, predicting system needs before they become failures.</li>
            </ul>
            <img
              className={"calisto-explain-img standard-box"}
              src={"/images/drones/callisto/callisto-explain2.jpg"}
            />
          </div>

          <h2 className={"section-head"}>Specifications</h2>
          <div className={"calisto-explain-cont"}>
            <p>
              To give eyes to this intelligence, Callisto features a modular mission bay with a payload capacity of up to 2 kg. 
              The platform is designed to accommodate a diverse array of optical and thermal systems tailored to the specific needs of the operators - 
              ranging from high-resolution survey cameras and precision LiDAR systems for detailed mapping to stabilised gimballed EO/IR units for day 
              and night surveillance.
            </p>
            <p>
              Callisto will, once again, be aimed at both survey and surveillance applications:
            </p>
            <ul className={"callisto-explain-points"}>
              <li>&#8226; Surveing tracks of land in excess of 2000 ha at a time, such as game counting, wildlife management or vegetation surveys over entire game reserves.</li>
              <li>&#8226; Border patrol, inshore coastal patrol, maritime patrol, etc.</li>
              <li>&#8226; Extended linear infrastructure protection, such as pipeline, powerline and railway line monitoring and security.</li>
            </ul>
            <p>
              The cognitive dominance of Callisto is matched by physical endurance. With a class-leading flight time of between 4 to 8 hours and a range 
              of up to 580 km, the 15 kg Callisto delivers the persistence required for vast border security and strategic surveying.
            </p>
            
            <div className={"drone-specs-tables-cont callisto-table-cont"}>
              <table className={"drone-specs-table callisto-table standard-box"}>
                <tbody>
                  <tr>
                    <td>Ceiling</td>
                    <td>10,000 ft</td>
                  </tr>
                  <tr>
                    <td>Weight</td>
                    <td>15 kg maximum gross take-off weight</td>
                  </tr>
                  <tr>
                    <td>Airspeed</td>
                    <td>
                      <ul>
                        <li>Loiter 50 km/h</li>
                        <li>Cruise 65 km/h</li>
                        <li>Dash 115 km/h</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td>Payload</td>
                    <td>
                      <ul>
                        <li>Modular mission bay</li>
                        <li>2 kg capacity</li>
                        <li>Accommodates a wide variety of optical and thermal systems</li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className={"drone-specs-table callisto-table standard-box"}>
                <tbody>
                  <tr>
                    <td>Endurance</td>
                    <td>
                      <ul>
                        <li>Standard Li-ion battery: 4+ hrs</li>
                        <li>Upgradable to Lithium Metal battery: 6+ hrs</li>
                        <li>Extender option with upgraded batteries: 8+ hrs</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td>Range</td>
                    <td>
                      <ul>
                        <li>Standard Li-ion battery: 250+ km</li>
                        <li>Upgradable to Lithium Metal battery: 380+ km</li>
                        <li>Extender option with upgraded batteries: 580+ km</li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td>Link</td>
                    <td>
                      Meshed datalink communicating or relaying with other systems, on ground or air units, in the network
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ================================== CTA ================================== */}
      <section className={"drone-cta-section standard-width"}>
        <p className={"section-head-pre"}>Chat with us</p>
        <h2 className={"section-head"}>Let Callisto expand your horizons</h2>
        <p className={"drone-cta-text"}>
          Get in touch with us today. Once our team fully understands your requirements,
          we can help prepare your business & team for a future with Callisto.
        </p>
        <Link to={PAGE_ROUTES.CONTACT}>
          <StandardButton
            label={"Enquire now"}
            className={"drone-cta-button"}
          />
        </Link>
        <img
          className={"drone-cta-img standard-box"}
          src={"/images/drones/callisto/Callisto_Swarm2.jpg"}
        />
      </section>
    </main>
  );
};

export default Calisto;